import { get } from "svelte/store";

import API from "../API/API";
import LoadState from "../Enums/LoadState";
import Router from "../Router";
import AlertType from "./AlertType";

import AuthStore from "../Auth/Store";
import MapStore from "../Map/Store";

import MapDataSource from "../Map/Enums/MapDataSource";

import VesselAlertDataPropertyMap from "../Models/Enums/VesselAlertDataPropertyMap";
import VesselProperty from "../Models/Enums/VesselProperty";
import Vessel from "../Models/Vessel";
import VesselGroup from "../Models/VesselGroup";

import GeoHelper from "../Helpers/GeoHelper";
import MapLayer from "../Map/Enums/MapLayer";
import MapLayerGroup from "../Map/Enums/MapLayerGroup";
import MapLayerManager from "../Map/MapLayerManager";
import { Layout, Paint } from "../Map/MapLayerStyles";
import AlertsStore from "./Store";

const AlertsActions = {};

// <initialisation>
// Called when App mounts, ensure execution order is preserved
// Unsubscribe after one execution
let initUnsubscribe;
AlertsActions.init = () => {
	if (AlertsStore.vesselGroup === null) {
		AlertsStore.vesselGroup = new VesselGroup();
	}

	initUnsubscribe = AlertsStore.vesselGroup.vessels.subscribe((data) => {
		if (data.length === 0) return;
		// TODO: refactor into loop
		AlertsStore.filters.alertTypes[AlertType.ERI].exists.set(AlertsStore.helpers.hasAlertType(AlertType.ERI));
		AlertsStore.filters.alertTypes[AlertType.LRI].exists.set(AlertsStore.helpers.hasAlertType(AlertType.LRI));
		AlertsStore.filters.alertTypes[AlertType.PCS].exists.set(AlertsStore.helpers.hasAlertType(AlertType.PCS));
		AlertsStore.filters.alertTypes[AlertType.LPO].exists.set(AlertsStore.helpers.hasAlertType(AlertType.LPO));
		AlertsStore.filters.alertTypes[AlertType.O7D].exists.set(AlertsStore.helpers.hasAlertType(AlertType.O7D));
		AlertsStore.filters.alertTypes[AlertType.O1D].exists.set(AlertsStore.helpers.hasAlertType(AlertType.O1D));
		AlertsStore.filters.alertTypes[AlertType.O2D].exists.set(AlertsStore.helpers.hasAlertType(AlertType.O2D));
		AlertsStore.filters.alertTypes[AlertType.O22H].exists.set(AlertsStore.helpers.hasAlertType(AlertType.O22H));
		AlertsStore.filters.alertTypes[AlertType.ABO].exists.set(AlertsStore.helpers.hasAlertType(AlertType.ABO));
		AlertsStore.filters.alertTypes[AlertType.CAS].exists.set(AlertsStore.helpers.hasAlertType(AlertType.CAS));
		AlertsStore.filters.alertTypes[AlertType.ETW].exists.set(AlertsStore.helpers.hasAlertType(AlertType.ETW));
		AlertsStore.filters.alertTypes[AlertType.LTW].exists.set(AlertsStore.helpers.hasAlertType(AlertType.LTW));
		AlertsStore.filters.alertTypes[AlertType.EEZ].exists.set(AlertsStore.helpers.hasAlertType(AlertType.EEZ));
		AlertsStore.filters.alertTypes[AlertType.LEZ].exists.set(AlertsStore.helpers.hasAlertType(AlertType.LEZ));
		AlertsStore.filters.alertTypes[AlertType.MYF].exists.set(AlertsStore.helpers.hasAlertType(AlertType.MYF));
		AlertsStore.filters.alertTypes[AlertType.STS].exists.set(AlertsStore.helpers.hasAlertType(AlertType.STS));
		AlertsStore.filters.alertTypes[AlertType.NBY].exists.set(AlertsStore.helpers.hasAlertType(AlertType.NBY));
		AlertsStore.filters.alertTypes[AlertType.RIM].exists.set(AlertsStore.helpers.hasAlertType(AlertType.RIM));
		AlertsStore.filters.alertTypes[AlertType.DEC].exists.set(AlertsStore.helpers.hasAlertType(AlertType.DEC));
		AlertsStore.filters.alertTypes[AlertType.COF].exists.set(AlertsStore.helpers.hasAlertType(AlertType.COF));
		AlertsStore.filters.lastUpdated.set(new Date());

		Router.run();

		// AlertsStore initiaisation complete, remove subscription
		initUnsubscribe();
	});
};
AlertsActions.initMap = (map) => {
	if (get(AlertsStore.meta.mapInitted)) {
		return;
	}
	map.addSource(MapDataSource.LIVE_VESSELS, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});
	map.addSource(MapDataSource.HISTORIC_LINES, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});
	map.addSource(MapDataSource.HISTORIC_POINTS, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});
	map.addSource(MapDataSource.VESSELS_LIVE_NEARBY_BOUNDS, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});

	map.addLayer({
		id: MapLayer.VESSELS_LIVE,
		type: "symbol",
		source: MapDataSource.LIVE_VESSELS,
		layout: Layout.vessel
	});
	map.addLayer(
		{
			id: MapLayer.VESSELS_HISTORY_LINES,
			type: "line",
			source: MapDataSource.HISTORIC_LINES,
			paint: Paint.vesselTrails
		},
		MapLayer.VESSELS_LIVE // "hillshade"
	);
	map.addLayer(
		{
			id: MapLayer.VESSELS_HISTORY_POINTS,
			type: "symbol",
			source: MapDataSource.HISTORIC_POINTS,
			layout: Layout.historic
		},
		MapLayer.VESSELS_LIVE
	);
	map.addLayer({
		id: MapLayer.VESSELS_LIVE_NEARBY_BOUNDS,
		type: "line",
		source: MapDataSource.VESSELS_LIVE_NEARBY_BOUNDS,
		layout: Layout.measure,
		paint: Paint.nearbyBounds.lines
	});
	map.setLayerZoomRange(MapLayer.VESSELS_HISTORY_LINES, 0, 24);
	map.setLayerZoomRange(MapLayer.VESSELS_HISTORY_POINTS, 0, 24);

	MapLayerManager.addLayerGroup(
		MapLayerGroup.ALERTS,
		[
			MapLayer.VESSELS_LIVE,
			MapLayer.VESSELS_HISTORY_LINES,
			MapLayer.VESSELS_HISTORY_POINTS,
			MapLayer.VESSELS_LIVE_NEARBY_BOUNDS
		],
		[
			MapDataSource.LIVE_VESSELS,
			MapDataSource.HISTORIC_LINES,
			MapDataSource.HISTORIC_POINTS,
			MapDataSource.VESSELS_LIVE_NEARBY_BOUNDS
		]
	);

	AlertsStore.meta.mapInitted.set(true);
	AlertsStore.meta.lastUpdated.set(new Date(0));
};
AlertsActions.deinitMap = () => {
	MapLayerManager.removeLayerGroup(MapLayerGroup.ALERTS);
	AlertsStore.meta.mapInitted.set(false);
};
// </initialisation>

// <actions>
/** Fetch, processes, store data **/
AlertsActions.addAlertTypeSubcategoryIfNotExist = (alertType, categoryName) => {
	const currentCategories = get(AlertsStore.filters.alertTypes[alertType].subcategories);
	let found = false;
	let currentCategory = null;
	for (let i = 0; i < currentCategories.length; i++) {
		if (currentCategories[i].name === categoryName) {
			currentCategory = currentCategories[i];
			found = true;
			break;
		}
	}
	if (!found) {
		currentCategory = AlertsStore.generators.filterTypeSubcategory(categoryName);
		currentCategories.push(currentCategory);
	}
	AlertsStore.filters.alertTypes[alertType].subcategories.set(currentCategories);
};
AlertsActions.loadNewRemoteData = () => {
	AlertsStore.actions.setLoadState(LoadState.LOADING);

	// const USE_DEBUG_DATA = useDebugData === "USE_DEBUG_DATA";
	// const urls = get(identity).urls;
	// if (IdentityHelper.isGeollect() && USE_DEBUG_DATA) {
	// console.warn("*DEBUG DATA IN USE* can be toggled off inside this script");
	// return new Promise((resolve) => resolve(debugData));
	// }

	let hasHistoricData = false;

	// Only load AlertTypes from user data
	const alertTypes = get(AuthStore.session.alertTypes).map((alertType) => {
		return alertType.code;
	});
	const requests = [];
	for (let i = 0; i < alertTypes.length; i++) {
		if (get(AuthStore.userInfo).forceIdentityId !== -1) {
			requests.push(API.alerts(alertTypes[i], get(AuthStore.userInfo).forceIdentityId));
		}
		else {
			requests.push(API.alerts(alertTypes[i]));
		}
	}
	Promise.all(requests).then((responses) => {
		const vesselObjects = [];

		for (let i = 0; i < responses.length; i++) {
			const alertType = alertTypes[i];
			const alertsForType = responses[i].alerts[alertType];
			if (!alertsForType) {
				console.warn("alerts for type ", alertType, "likely not set up for company");
				continue;
			}
			for (let j = 0; j < alertsForType.length; j++) {
				const alertCategory = alertsForType[j];
				const live = alertCategory.live;
				const historic = alertCategory.historic;
				let alertCategoryName = AlertType.DEFAULT_CATEGORY_NAME;
				if (alertCategory.name) {
					alertCategoryName = alertCategory.name;
				}
				AlertsActions.addAlertTypeSubcategoryIfNotExist(alertType, alertCategoryName);
				// add it to categories for alertType?

				if (!live) {
					console.warn("live", live, "for alerttype", alertType, "optimise this away");
					continue;
				}
				if (live.features) {
					live.features.forEach((feature) => {
						if (!AlertsStore.helpers.isValidVesselFeature(feature)) {
							console.warn("Invalid feature passed in fetched data", feature);
							return;
						}
						// Don't add a new vessel if there's already one in vesselObjects, just update
						// (this is to minimise load on updating the live app's data)
						// const thisImo = feature.properties[VesselAlertDataPropertyMap[VesselProperty.IMO]];
						const thisMmsi = feature.properties[VesselAlertDataPropertyMap[VesselProperty.MMSI]];
						const matchesInVesselObjects = vesselObjects.filter(
							(vesselObject) => vesselObject.mmsi === thisMmsi
						);
						if (matchesInVesselObjects.length > 0) {
							matchesInVesselObjects[0].addAlertData(feature, alertType, alertCategoryName);
						}
						else {
							let vessel;
							// See if object's already been generated and used in map. Update and add if so.
							const fromPreviousStore = AlertsStore.vesselGroup.find(
								{ [VesselProperty.MMSI]: thisMmsi },
								false
							);
							if (fromPreviousStore) {
								fromPreviousStore.addAlertData(feature, alertType, alertCategoryName);
								vessel = fromPreviousStore;
							}
							else {
								// No existing object, generate and add features
								vessel = new Vessel(
									feature,
									alertType,
									alertCategoryName,
									AlertsStore.vesselGroup,
									VesselAlertDataPropertyMap
								);
								// vessel.setAlertCategory
								vessel.setUniqIdType(VesselProperty.MMSI);
								if (historic) {
									const historicFeatures = historic.features.filter(
										(historicVessel) =>
											historicVessel.properties[
												VesselAlertDataPropertyMap[VesselProperty.MMSI]
											] === thisMmsi
									);
									// console.log("alertType", alertType, "historicFeatures", historicFeatures);
									// console.log("historic features for vessel imo", vessel.imo, vessel.mmsi, feature.properties.mmsi, historicFeatures);
									// We don't need to downsample any more. It's done on the backend.
									// const downsampledData = AlertsStore.helpers.downsampleHistoricData(historicFeatures);
									vessel.generateHistoricPath(historicFeatures);
								}
								else {
									vessel.generateHistoricPath([]);
								}
							}
							if (vessel.hasHistoricData()) {
								hasHistoricData = true;
							}
							vesselObjects.push(vessel);
						}
					});
				}
			}
		}
		AlertsStore.vesselGroup.vessels.set(vesselObjects);
		AlertsStore.meta.hasHistoricData.set(hasHistoricData);
		AlertsStore.actions.setLoadState(LoadState.LOADED);
	});
};

AlertsActions.removeAllVessels = () => {
	AlertsStore.vesselGroup.vessels.set([]);
	AlertsStore.actions.setLoadState(LoadState.INIT);
};
AlertsActions.setLoadState = (loadState) => {
	const lastUpdated = get(AlertsStore.meta.lastUpdated);
	const newLoadState = lastUpdated.getTime() === 0 ? loadState : get(AlertsStore.meta.loadState);
	const newUpdateState = loadState;
	const newLastUpdated = loadState === LoadState.LOADED ? new Date() : lastUpdated;

	// console.log("Alerts setLoadState", loadState, newLoadState, newUpdateState, lastUpdated, newLastUpdated);
	AlertsStore.meta.loadState.set(newLoadState);
	AlertsStore.meta.updateState.set(newUpdateState);
	AlertsStore.meta.lastUpdated.set(newLastUpdated);
};

AlertsActions.setAlertTypeVisible = (type, visible) => {
	AlertsStore.filters.alertTypes[type].visible.set(visible);
	get(AlertsStore.vesselGroup.vessels).forEach((vessel) => {
		vessel.setIconMode(get(AlertsStore.iconMode)); // Update the icon! This code is bad!
	});
	AlertsStore.filters.lastUpdated.set(new Date());
	AlertsStore.actions.updateMapSources();

	// AlertsStore.helpers.printFilterState();
};
AlertsActions.setAlertTypeSubcategoryVisible = (alertType, subcategory, visible) => {
	const subcategories = get(AlertsStore.filters.alertTypes[alertType].subcategories);
	for (let i = 0; i < subcategories.length; i++) {
		if (subcategories[i].name === subcategory) {
			subcategories[i].visible.set(visible);
		}
	}
	get(AlertsStore.vesselGroup.vessels).forEach((vessel) => {
		vessel.setIconMode(get(AlertsStore.iconMode)); // Update the icon! This code is bad!
	});
	AlertsStore.filters.lastUpdated.set(new Date());
	AlertsStore.actions.updateMapSources();

	// AlertsStore.helpers.printFilterState();
};

// AlertsActions.setVesselSelectedByImo = (imo, setToSelected = true, deselectExisting) => {
// 	AlertsActions.setVesselSelectionStateByImo(imo, setToSelected, deselectExisting);
// };

// This is used to deselect a vessel on the map, and this occurs when re-clicking a vessel that is selected.
// AlertsActions.setVesselUnselectedByImo = imo => {
// 	AlertsActions.setVesselSelectionStateByImo(imo, false);
// };

// AlertsActions.setVesselSelectionStateByImo = (imo, setToSelected, deselectExisting, updateWhenDone = true) => {
// 	if (deselectExisting) {
// 		AlertsStore.vesselGroup.deselectAll(); // AlertsActions.deselectAllVessels();
// 	}
// 	const vessel = AlertsStore.vesselGroup.find({ imo: imo });
// 	if (vessel) {
// 		vessel.selected = setToSelected;
// 	}
// 	else {
// 		console.warn("vessel imo was not found", imo);
// 	}
// 	if (updateWhenDone) {
// 		AlertsStore.vesselGroup.update(); // AlertsStore.vessels.update(value => value); // trigger update
// 	}
// };

// AlertsActions.setVesselsSelectedByIMOs = (imos = [], deselectExisting = false, updateWhenDone = false) => {
// 	if (imos.length <= 0 || deselectExisting) {
// 		AlertsStore.vesselGroup.deselectAll(); // AlertsActions.deselectAllVessels();
// 	}
// 	imos.forEach(imo =>
// 		AlertsActions.setVesselSelectionStateByImo(imo, true, false, updateWhenDone)
// 	);
// 	AlertsStore.vesselGroup.update(); // AlertsStore.vessel.update(value => value); // trigger update
// };

// AlertsActions.deselectAllVessels = () => {

// };

AlertsActions.updateMapSources = () => {
	// TODO: if this is called a lot or takes long, it should be cached (or perhaps at GetGeoJson level)
	if (!AlertsStore._localCaches.mapReady) {
		AlertsStore._localCaches.updateMapSourcesRequestRejected = true;
		console.log("🔄 Refreshing sources REJECTED");
		return;
	}
	if (!get(AlertsStore.meta.mapInitted)) {
		console.log("🔄 Refreshing sources REJECTED (no map layers)");
		return;
	}
	console.log("🔄 Refreshing sources...");
	const vesselGeoJson = AlertsStore.helpers.GetGeoJson.vessels.display();
	const pathGeoJson = AlertsStore.helpers.GetGeoJson.paths.display();
	const nearbyBoundsGeoJson = AlertsStore.helpers.GetGeoJson.nearbyBounds.display();
	const map = get(MapStore.mapbox.instance);

	map.getSource(MapDataSource.LIVE_VESSELS).setData(vesselGeoJson);
	map.getSource(MapDataSource.HISTORIC_LINES).setData(pathGeoJson.lines);
	map.getSource(MapDataSource.HISTORIC_POINTS).setData(pathGeoJson.points);

	map.getSource(MapDataSource.VESSELS_LIVE_NEARBY_BOUNDS).setData(nearbyBoundsGeoJson);
};
// </actions>

export default AlertsActions;
